import React, { useEffect, useState } from "react";

import { formatPhoneDisplay } from "../../utils/formatPhone";
import { formatPatientName } from "../../utils/formatName";

// Demographics, and the one place a biller can correct them.
//
// The editable set is exactly the backend's whitelist. Insurance is not in it:
// the payer and member id belong to a coverage, and editing them here would edit
// a policy in place, which is the one thing Coverage's contract forbids. The
// backend refuses those fields with a 400 rather than ignoring them, so the two
// lists have to agree — if they drift, the biller gets an error instead of a
// silent no-op, which is the failure mode worth having.

const FIELDS = [
  { key: "patient_first_name", label: "First name" },
  { key: "patient_last_name", label: "Last name" },
  { key: "patient_dob", label: "Date of birth", type: "date" },
  {
    key: "patient_gender",
    label: "Sex",
    type: "select",
    options: ["", "M", "F", "U"],
  },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email", type: "email" },
  { key: "address1", label: "Address" },
  { key: "address2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State", maxLength: 2 },
  { key: "zip", label: "ZIP", maxLength: 10 },
];

const ADDRESS_KEYS = ["address1", "address2", "city", "state", "zip"];

// The MRN is the EMR's own id for the patient — one identifier, not two. Older
// rows that never carried an EMR id fall back to the imported patient id.
export function patientMrn(patient) {
  return (patient && (patient.emr_patient_id || patient.patient_id)) || "";
}

// Names read in proper case, whatever case the EMR exported them in.
const NAME_KEYS = ["patient_first_name", "patient_last_name"];

function displayValue(patient, key) {
  if (key === "phone") return formatPhoneDisplay(patient.phone);
  if (NAME_KEYS.includes(key) && patient[key]) return formatPatientName(patient[key]);
  return patient[key];
}

// "1 Main St, Apt 2, Fresno, CA 93701" — the five stored parts on one line.
function addressLine(patient) {
  const street = [patient.address1, patient.address2].filter(Boolean);
  const stateZip = [patient.state, patient.zip].filter(Boolean).join(" ");
  return [...street, patient.city, stateZip].filter(Boolean).join(", ");
}

// The read view: the edit form's fields, with the address collapsed to one line,
// the phone formatted, and the MRN (shown, never edited here; a coverage owns the
// member id).
function displayRows(patient) {
  const rows = FIELDS.filter(({ key }) => !ADDRESS_KEYS.includes(key)).map(
    ({ key, label }) => ({
      key,
      label,
      value: displayValue(patient, key),
    }),
  );
  rows.push({ key: "address", label: "Address", value: addressLine(patient) });
  rows.push({ key: "mrn", label: "MRN", value: patientMrn(patient) });
  return rows;
}

function formValues(patient) {
  const values = {};
  FIELDS.forEach(({ key }) => {
    values[key] = patient[key] || "";
  });
  return values;
}

export default function PatientCard({ patient, canEdit, busy, onSave }) {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState(() => formValues(patient));

  // A different patient in the same drawer must not inherit the last one's
  // half-typed edit. Keyed on the id alone on purpose: re-running this whenever
  // the patient object is replaced would throw away an in-progress edit every
  // time the summary refreshes.
  const patientId = patient.id;
  useEffect(() => {
    setValues(formValues(patient));
    setEditing(false);
    // eslint-disable-next-line
  }, [patientId]);

  const set = (key) => (event) =>
    setValues((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = (event) => {
    event.preventDefault();
    // Send only what changed. A field the biller never touched should not be
    // re-asserted, and an empty one they never touched should not be cleared.
    const changed = {};
    FIELDS.forEach(({ key }) => {
      if (values[key] !== (patient[key] || "")) changed[key] = values[key];
    });
    if (Object.keys(changed).length === 0) {
      setEditing(false);
      return;
    }
    onSave(changed, () => setEditing(false));
  };

  return (
    <section className="pt-card">
      <div className="pt-card-head">
        {/* Cash pay is `practice_patients.payment_type = 'self_pay'` (ruling R1) — the
            same column the readiness check, the collections list and the cash-pay
            charge template read. Shown here so nobody hunts for insurance that was
            never meant to exist. */}
        {(patient.payment_type || "").toLowerCase() === "self_pay" && (
          <span className="pt-chip pt-chip-muted">Cash pay</span>
        )}
        {canEdit && !editing && (
          <button
            className="pt-btn pt-card-head-end"
            onClick={() => setEditing(true)}
          >
            Edit details
          </button>
        )}
      </div>

      {editing ? (
        <form className="pt-form" onSubmit={submit}>
          <div className="pt-form-grid">
            {FIELDS.map((field) => (
              <label key={field.key}>
                {field.label}
                {field.type === "select" ? (
                  <select value={values[field.key]} onChange={set(field.key)}>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option || "—"}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type || "text"}
                    value={values[field.key]}
                    maxLength={field.maxLength}
                    onChange={set(field.key)}
                  />
                )}
              </label>
            ))}
          </div>
          <div className="pt-form-actions">
            <button
              type="button"
              className="pt-btn"
              disabled={busy}
              onClick={() => {
                setValues(formValues(patient));
                setEditing(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pt-btn pt-btn-primary"
              disabled={busy}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      ) : (
        <dl className="pt-details">
          {displayRows(patient).map((row) => (
            <div
              className={`pt-detail${row.key === "address" ? " pt-detail-wide" : ""}`}
              key={row.key}
            >
              <dt>{row.label}</dt>
              <dd>{row.value || "—"}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
